import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, Trash2, RefreshCw, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtError = (detail) => {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiries, setEnquiries] = useState([]);

  const loadEnquiries = useCallback(async () => {
    const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
    setEnquiries(data);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        return loadEnquiries();
      })
      .catch(() => setUser(false));
  }, [loadEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      await loadEnquiries();
    } catch (err) {
      setError(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setEnquiries([]);
  };

  const remove = async (id) => {
    await axios.delete(`${API}/enquiries/${id}`, { withCredentials: true });
    setEnquiries((list) => list.filter((e) => e.id !== id));
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-deep" data-testid="admin-loading">
        <Loader2 className="animate-spin text-white" size={28} />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="blueprint-grid-dark flex min-h-screen items-center justify-center bg-navy-deep px-5">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-white/20 bg-white p-8"
          style={{ boxShadow: "6px 6px 0 0 #0055FF" }}
        >
          <span className="flex h-11 w-11 items-center justify-center bg-navy text-white">
            <Lock size={18} />
          </span>
          <h1 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-navy">Owner Login</h1>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Subhankara — Enquiries Dashboard
          </p>
          <label htmlFor="admin-email" className="mb-1.5 mt-6 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 w-full border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-electric"
          />
          <label htmlFor="admin-password" className="mb-1.5 mt-4 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 w-full border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-electric"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 border-l-2 border-red-500 pl-3 text-xs font-semibold text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={busy}
            className="mt-6 flex w-full items-center justify-center gap-2 bg-electric py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-navy disabled:opacity-60"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Signing in..." : "Sign In"}
          </button>
          <a href="/" data-testid="admin-back-link" className="mt-4 block text-center font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:text-electric">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="border-b border-navy/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Subhankara logo" className="h-9 w-9 border border-navy/15 object-contain" />
            <div>
              <p className="font-display text-sm font-extrabold text-navy">Enquiries Dashboard</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={loadEnquiries}
              className="flex items-center gap-2 border border-navy px-3.5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-white"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 bg-navy px-3.5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-electric"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="enquiry-count">
          {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"} received
        </p>
        <div className="mt-5 space-y-4">
          {enquiries.length === 0 && (
            <p className="border border-dashed border-slate-300 bg-white p-10 text-center font-mono text-xs uppercase tracking-[0.15em] text-slate-400" data-testid="enquiries-empty">
              No enquiries yet
            </p>
          )}
          {enquiries.map((e) => (
            <article key={e.id} data-testid={`enquiry-row-${e.id}`} className="border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold text-navy">{e.name}</h3>
                  <p className="mt-0.5 font-mono text-xs text-slate-500">
                    {e.phone} · {e.service}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-slate-400">
                    {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                  <button
                    data-testid={`enquiry-delete-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="border border-red-200 p-2 text-red-500 transition-colors hover:bg-red-500 hover:text-white"
                    aria-label="Delete enquiry"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="mt-3 border-l-2 border-electric pl-3 text-sm leading-relaxed text-slate-600">{e.message}</p>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
